import { useCallback } from 'react';

import toast from 'react-hot-toast';
import { Edge, Node, useReactFlow } from 'reactflow';
import { useMutation } from '@tanstack/react-query';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import useProject from 'hooks/useProject';
import { hyperparameterState, isProjectChangeState, shouldUpdateCodeState } from 'state/atoms';
import updateModelHyperparameter from 'api/project/updateModelHyperparameter';
import { FLOW_TYPE } from 'constants/flow';
import useModal from 'hooks/useModal';
import BlockNotConnectedDialog from 'components/dialogs/BlockNotConnectedDialog';
import BlockParamNotEnteredDialog from 'components/dialogs/BlockParamNotEnteredDialog';

function useModelSave() {
  const { data: projectData, update } = useProject();
  const modal = useModal();

  const { getViewport, getEdges, getNodes } = useReactFlow();
  const hyperparameter = useRecoilValue(hyperparameterState);
  const setIsProjectChange = useSetRecoilState(isProjectChangeState);
  const setShouldUpdateCode = useSetRecoilState(shouldUpdateCodeState);

  const { mutate: saveMutate, isPending } = useMutation({
    mutationFn: updateModelHyperparameter,
  });

  const save = useCallback(
    (callBack?: () => void) => {
      if (!projectData?.result) return;

      const saveMutateFn = (nodes: Node[], edges: Edge[]) => {
        saveMutate(
          {
            projectName: projectData.projectName,
            ownerName: projectData.ownerName,
            hyperparameter: hyperparameter,
            model: {
              nodes,
              edges,
              viewport: getViewport(),
            },
          },
          {
            onSuccess: (data, { model, hyperparameter }) => {
              update({
                model,
                hyperparameter,
                ...(data.generateCheck && { code: data.code }),
              });

              if (callBack) {
                callBack();
              }

              toast.success('저장되었습니다');

              if (!data.generateCheck) {
                toast.error('코드 생성에 실패했습니다');
              }

              setIsProjectChange(false);
              setShouldUpdateCode(false);
            },
          }
        );
      };

      const nodeMap = {} as { [nodeId: string]: boolean };

      let allParamFill = true;
      let totalConnectableNode = 0;

      const nodes = getNodes().map((node) => {
        if (node.type === FLOW_TYPE.NODE.LAYER_NODE || node.type === FLOW_TYPE.NODE.DATA_NODE) {
          totalConnectableNode++;
        }
        if (node.type === FLOW_TYPE.NODE.LAYER_NODE) {
          node.data.parameters.forEach((parameter: { value: any; defaultValue: any }) => {
            if (parameter.defaultValue !== '_Optional' && !parameter.value && parameter.value !== 0) {
              allParamFill = false;
            }
          });
        }
        return {
          ...node,
          selected: false,
        };
      });

      const edges = getEdges().map((edge) => {
        nodeMap[edge.source] = true;
        nodeMap[edge.target] = true;
        return {
          ...edge,
          selected: false,
        };
      });

      if (Object.keys(nodeMap).length !== totalConnectableNode) {
        return modal.render({
          key: 'blockNotConnectedDialog',
          modal: <BlockNotConnectedDialog onConfirm={() => saveMutateFn(nodes, edges)} />,
        });
      }

      if (!allParamFill) {
        return modal.render({
          key: 'blockParamNotEnteredDialog',
          modal: <BlockParamNotEnteredDialog onConfirm={() => saveMutateFn(nodes, edges)} />,
        });
      }

      saveMutateFn(nodes, edges);
    },
    [
      projectData,
      modal,
      hyperparameter,
      update,
      saveMutate,
      getEdges,
      getNodes,
      getViewport,
      setIsProjectChange,
      setShouldUpdateCode,
    ]
  );

  const handleSaveClick = useCallback(() => {
    save();
  }, [save]);

  return {
    save,
    onSaveClick: handleSaveClick,
    isLoading: isPending,
  };
}

export default useModelSave;
